import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, House, Lock } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { gbp, longDate } from "@/lib/format";
import { QUESTIONS } from "@/components/applicant/questions";
import { QuestionStep, StepActions, StepHeader } from "@/components/applicant/QuestionStep";
import { FlightPath } from "@/components/applicant/FlightPath";
import { JourneyPanel, PassLabel } from "@/components/applicant/JourneyPanel";
import { DocumentItem } from "@/components/applicant/DocumentItem";
import { BankStage } from "@/components/applicant/BankStage";
import { ReviewStage } from "@/components/applicant/ReviewStage";
import { DoneStage } from "@/components/applicant/DoneStage";
import { InlineError, TenantLoadError, TenantLoading, TenantShell } from "@/components/applicant/TenantShell";
import { PrimaryButton } from "@/components/shared/ui";

const ITINERARY = [
  ["Your ID", "Your passport or photo driving licence"],
  ["Your income", "How you're paid, and the documents that show it"],
  ["Anyone helping with the rent", "A person sharing the rent with you, or a guarantor"],
  ["Check and send", "One look over everything, then it goes to the agent"],
];

const BoardingPass = ({ let: l }) => (
  <div className="relative mt-8 overflow-hidden rounded-3xl border bg-background md:mt-0" data-testid="intro-pass">
    <div className="px-5 pb-4 pt-5">
      <div className="flex items-start justify-between gap-6">
        <div>
          <PassLabel>From</PassLabel>
          <p className="mt-1 text-base font-semibold">Today</p>
        </div>
        <div className="min-w-0 text-right">
          <PassLabel>To</PassLabel>
          <p className="mt-1 text-base font-semibold leading-snug" data-testid="intro-address">
            {l.address.includes(",") ? <>{l.address.slice(0, l.address.lastIndexOf(","))}<span className="block text-sm font-normal text-muted-foreground">{l.address.slice(l.address.lastIndexOf(",") + 1).trim()}</span></> : l.address}
          </p>
        </div>
      </div>
      <FlightPath t={0} stops={[0.25, 0.5, 0.75]} className="mt-3" testid="intro-flight-path" />
    </div>
    <div className="relative border-t border-dashed">
      <span className="absolute -left-3 -top-3 size-6 rounded-full border bg-panel" />
      <span className="absolute -right-3 -top-3 size-6 rounded-full border bg-panel" />
    </div>
    <dl className="grid grid-cols-2 gap-4 px-5 py-4">
      <div><dt><PassLabel>Rent</PassLabel></dt><dd className="mt-1 text-sm font-medium">{gbp(l.monthly_rent)} a month</dd></div>
      <div className="text-right"><dt><PassLabel>Move in</PassLabel></dt><dd className="mt-1 text-sm font-medium">{longDate(l.move_in_date)}</dd></div>
    </dl>
  </div>
);

const Intro = ({ data, onStart }) => (
  <div className="rise md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-x-14 md:pt-6" data-testid="applicant-intro">
    <div className="md:col-start-1 md:row-start-1">
      <PassLabel>{data.let.agency_name} · Rental application</PassLabel>
      <h1 className="mt-3 text-[30px] font-semibold leading-[1.1] tracking-tight md:text-[40px]">Let's get you into your new home</h1>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground md:mt-4 md:text-lg">A few short stops. Each document is checked in seconds, so you'll know straight away if anything needs fixing.</p>
    </div>
    <div className="md:col-start-2 md:row-span-3 md:row-start-1">
      <BoardingPass let={data.let} />
      <div className="hidden md:block">
        <PrimaryButton size="xl" className="mt-6 w-full" onClick={onStart} testid="intro-start-button-desktop">Start <ArrowRight /></PrimaryButton>
        <p className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Lock className="size-4 shrink-0" /> No account needed. This private link saves your progress.</p>
      </div>
    </div>
    <ol className="mt-8 flex flex-col md:col-start-1 md:row-start-2 md:mt-10" data-testid="intro-itinerary">
      {ITINERARY.map(([title, text], i) => (
        <li key={title} className="relative flex gap-4 pb-5 last:pb-0">
          {i < ITINERARY.length - 1 && <span className="absolute left-[11px] top-7 h-[calc(100%-28px)] border-l-[1.5px] border-dotted border-muted-foreground/40" />}
          <span className="grid size-6 shrink-0 place-items-center rounded-full border-[1.5px] border-foreground text-xs font-semibold tabular-nums">{i + 1}</span>
          <div className="pt-0.5">
            <p className="text-base font-medium">{title}</p>
            <p className="text-sm text-muted-foreground">{text}</p>
          </div>
        </li>
      ))}
      <li className="relative flex gap-4 pt-5">
        <span className="absolute left-[11px] top-0 h-5 border-l-[1.5px] border-dotted border-muted-foreground/40" />
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand text-brand-foreground"><House className="size-3.5" /></span>
        <p className="pt-0.5 text-base font-medium">Your new home</p>
      </li>
    </ol>
    <div className="md:col-start-1 md:row-start-3">
      <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground md:hidden"><Lock className="size-4 shrink-0" /> No account needed. This private link saves your progress.</p>
      <a href={`/criteria/${data.let.link_token}`} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm font-medium underline underline-offset-4 md:mt-8" data-testid="intro-criteria-link">Read the selection criteria</a>
    </div>
    <StepActions className="md:hidden"><PrimaryButton size="xl" className="w-full" onClick={onStart} testid="intro-start-button">Start <ArrowRight /></PrimaryButton></StepActions>
  </div>
);

const isFresh = (data) => !QUESTIONS.some((q) => data.answers[q.key]) && !data.checklist.some((i) => i.documents.length);
const nextKey = (steps, key) =>
  steps.slice(steps.findIndex((s) => s.key === key) + 1).find((s) => s.kind !== "review" && ["todo", "needs_fixing"].includes(s.status))?.key || "review";

/** The screening flow: one question or one document request per screen, in the order the server gives. */
export default function ApplicantFlow() {
  const { token, stepKey } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/a/${token}`);
      setData(d);
      setError("");
    } catch (err) {
      setError(errorText(err));
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const checking = data?.steps.some((s) => s.status === "checking");
  useEffect(() => {
    if (!checking) return undefined;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [checking, load]);

  const go = (key) => {
    navigate(`/a/${token}/${key}`);
    window.scrollTo({ top: 0 });
  };

  const answer = async (key, value) => {
    try {
      const { data: d } = await api.put(`/a/${token}/answers`, { answers: { [key]: value } });
      setData(d);
      go(nextKey(d.steps, key));
    } catch (err) {
      setError(errorText(err));
    }
  };

  const later = async (key) => {
    try {
      const { data: d } = await api.post(`/a/${token}/steps/${key}/later`);
      setData(d);
      go(nextKey(d.steps, key));
    } catch (err) {
      setError(errorText(err));
    }
  };

  let body;
  if (!data) {
    body = error ? <TenantLoadError error={error} onRetry={load} testid="applicant-load-error" /> : <TenantLoading />;
  } else if (data.submission.submitted_at) {
    body = <div className="mx-auto max-w-xl md:pt-6"><DoneStage data={data} /></div>;
  } else if (!stepKey) {
    body = isFresh(data) ? <Intro data={data} onStart={() => go(data.current_step)} /> : <Navigate to={`/a/${token}/${data.current_step}`} replace />;
  } else {
    const index = data.steps.findIndex((s) => s.key === stepKey);
    if (index === -1) {
      body = <Navigate to={`/a/${token}/${data.current_step}`} replace />;
    } else {
      const step = data.steps[index];
      const item = data.checklist.find((i) => i.key === step.key);
      const next = () => go(nextKey(data.steps, step.key));
      const sending = step.kind === "review" && search.get("part") === "send";
      let screen;
      if (step.kind === "question") {
        const q = QUESTIONS.find((x) => x.key === step.key);
        screen = <QuestionStep q={q} current={data.answers[q.key]} onAnswer={answer} />;
      } else if (step.kind === "bank") {
        screen = <BankStage data={data} token={token} reload={load} onNext={next} onLater={() => later(step.key)} />;
      } else if (step.kind === "document") {
        screen = <DocumentItem key={step.key} item={item} token={token} onUpdated={load} onContinue={next} onLater={() => later(step.key)} />;
      } else {
        screen = <ReviewStage data={data} token={token} setData={setData} part={sending ? "send" : "check"} onNext={go} onContinue={() => go(`${step.key}?part=send`)} />;
      }
      const back = sending ? () => go(step.key) : index > 0 ? () => go(data.steps[index - 1].key) : null;
      body = (
        <div className="md:grid md:grid-cols-[260px_minmax(0,1fr)] md:gap-14 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-20" data-testid={`step-${step.key}`} data-step-kind={step.kind}>
          <JourneyPanel data={data} token={token} index={index} />
          <div className="min-w-0 md:max-w-xl">
            <StepHeader steps={data.steps} index={index} onBack={back} phase={step.kind === "review" && sending ? 0.5 : 0}
              saveUrl={`${window.location.origin}/a/${token}`} />
            {screen}
          </div>
        </div>
      );
    }
  }

  return (
    <TenantShell agency={data?.let.agency_name}>
      {data && error && <InlineError testid="applicant-inline-error">{error}</InlineError>}
      {body}
    </TenantShell>
  );
}
